import React, {
  memo,
  useEffect, useReducer, useState
} from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  Row,
  Col,
  Button,
  Modal,
  ModalBody,
  ModalHeader,
  Spinner,
  Alert,
  Input
} from "reactstrap";
import { Accordion } from "react-bootstrap";
import { AvField, AvForm } from "availity-reactstrap-validation";
import { fetchIbParents } from "store/client/actions";
import { createSharedIbAgreement } from "store/actions";
import validatePositiveInputs from "helpers/validatePositiveInputs";
import AvFieldSelect from "components/Common/AvFieldSelect";
import { getAccountType } from "./helpers";
import { getProductCategoryLabel } from "common/productCategories";

const toSafeNumber = (raw) => {
  if (raw === "" || raw === null || raw === undefined) return 0;
  const parsed = parseFloat(raw);
  return Number.isNaN(parsed) ? 0 : parsed;
};

const AgreementTotals = ({ type, dispatcher, agreementName }) => {
  return (<Row key={type._id} className="my-1 align-items-center">
    <Col md="3">
      {agreementName}
    </Col>
    <Col>
      <Input
        bsSize="sm"
        type="text"
        value={type.rebate === "" ? 0 : type.rebate}
        onKeyPress={(e) => validatePositiveInputs(e)}
        onChange={(e) => {
          dispatcher({
            type: "UPDATE_TOTAL_REBATE",
            payload: {
              accountTypeId: type.accountTypeId,
              rebate: e.target.value,
            },
          });
        }}
      />
    </Col>
    <Col>
      <Input
        className="mt-3"
        bsSize="sm"
        type="text"
        value={type.commission === "" ? 0 : type.commission}
        onKeyPress={(e) => validatePositiveInputs(e)}
        onChange={(e) => {
          dispatcher({
            type: "UPDATE_TOTAL_COMMISSION",
            payload: {
              accountTypeId: type.accountTypeId,
              commission: e.target.value,
            },
          });
        }}
      />
    </Col>
  </Row>);
};

const MemoizedAgreementTotals = memo(AgreementTotals);

const InvestorForm = ({ type, accIdx, dispatcher, agreementName, member }) => {
  const { t } = useTranslation();
  return (<Accordion.Item key={type.accountTypeId} eventKey={accIdx}>
    <Accordion.Header>
      <Row className="justify-content-center mb-2 fw-bold gx-0">
        <Col md="12" className="text-center">
          {agreementName}
        </Col>
      </Row>
    </Accordion.Header>
    <Accordion.Body>
      <Row>
        <Col md="3" className="text-center">
          {t("PAMM Profit Share")} %
        </Col>
        <Col md="9" className="text-center">
          <Input
            type="text"
            value={type.pamProfitShare}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_MEMBER_PAMM_PROFIT_SHARE",
                payload: {
                  accountTypeId: type.accountTypeId,
                  customerId: member.customerId,
                  pamProfitShare: e.target.value,
                },
              });
            }}
            onKeyPress={(e) => validatePositiveInputs(e)}
          />
        </Col>
      </Row>
    </Accordion.Body>
  </Accordion.Item>);
};

const AccountForm = ({ type, accIdx, dispatcher, agreementName, memberIdx, member, products, markups }) => {
  return (<Accordion.Item key={type.accountTypeId} eventKey={accIdx}>
    <Accordion.Header>
      <Row className="w-100 my-1 align-items-center my-0">
        <Col md="3">
          {agreementName}
        </Col>
        <Col>
          <Input
            bsSize="sm"
            type="text"
            value={type.rebate === "" ? 0 : type.rebate}
            onKeyPress={(e) =>
              validatePositiveInputs(e)
            }
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_MEMBER_REBATE_TOTAL",
                payload: {
                  accountTypeId: type.accountTypeId,
                  rebate: e.target.value,
                  customerId: member.customerId,
                },
              });
            }}
          />
        </Col>
        <Col>
          <Input
            bsSize="sm"
            type="text"
            value={type.commission === "" ? 0 : type.commission}
            onKeyPress={(e) =>
              validatePositiveInputs(e)
            }
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_MEMBER_COMMISSION_TOTAL",
                payload: {
                  accountTypeId: type.accountTypeId,
                  commission: e.target.value,
                  customerId: member.customerId,
                },
              });
            }}
          />
        </Col>
      </Row>
    </Accordion.Header>
    <Accordion.Body>
      {Object.keys(type?.products ?? {})?.map((prod, prodIdx) => (
        <Row
          key={prodIdx}
          className="my-1 align-items-center"
        >
          <Col md="3">{getProductCategoryLabel(
            products.find((p) => p?.toLowerCase() === prod?.toLowerCase()) || prod
          )}</Col>
          <Col>
            <Input
              bsSize="sm"
              type="text"
              onKeyPress={(e) =>
                validatePositiveInputs(e)
              }
              value={type?.products?.[prod]?.rebate === "" ? 0 : (type?.products?.[prod]?.rebate ?? 0)}
              onChange={(e) => {
                dispatcher({
                  type: "UPDATE_MEMBER_REBATE_PRODUCT",
                  payload: {
                    accountTypeId: type.accountTypeId,
                    rebate: e.target.value,
                    customerId: member.customerId,
                    product: prod,
                  },
                });
              }}
            />
          </Col>
          <Col>
            <Input
              bsSize="sm"
              type="text"
              value={type?.products?.[prod]?.commission === "" ? 0 : (type?.products?.[prod]?.commission ?? 0)}
              onKeyPress={(e) =>
                validatePositiveInputs(e)
              }
              onChange={(e) => {
                dispatcher({
                  type: "UPDATE_MEMBER_COMMISSION_PRODUCT",
                  payload: {
                    accountTypeId: type.accountTypeId,
                    commission: e.target.value,
                    customerId: member.customerId,
                    product: prod,
                  },
                });
              }}
            />
          </Col>
        </Row>
      ))}
      <Row>
        <Col md="3">Markup</Col>
        <Col>
          <AvFieldSelect
            name={`members[${memberIdx}].values[${accIdx}].markup`}
            value={type?.markup}
            onChange={(markup) => {
              dispatcher({
                type: "UPDATE_MEMBER_MARKUP",
                payload: {
                  accountTypeId: type.accountTypeId,
                  customerId: member.customerId,
                  markup,
                },
              });
            }}
            options={(markups || []).map((obj) => {
              return ({
                label: `${obj}`,
                value: obj
              });
            })}
          />
        </Col>
      </Row>
    </Accordion.Body>
  </Accordion.Item>);
};

const MemoizedInvestorForm = memo(InvestorForm);
const MemoizedAccountForm = memo(AccountForm);

const AddSharedIbModal = ({
  show,
  toggle,
  accountTypes: allAccountTypes,
  products,
  clientId,
  markups,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [productsModel, setProductsModel] = useState({});
  const [submissionFailure, setSubmissionFailure] = useState(false);
  const [accountTypes, setAccountTypes] = useState([]);
  const [isAgreementValid, setIsAgreementValid] = useState(false);
  const [validationErrorMessage, setValidationErrorMessage] = useState("");
  const { submitting, clearingCounter, error: submitError } = useSelector((state) => state.ibAgreements);
  const initState = { 
    title: "",
    totals: [],
    members: [],
  };
  
  const { parents } = useSelector(
    (state) => state.clientReducer?.clientDetails
  );

  const [payload, dispatcher] = useReducer((state, action) => {
    switch (action.type) {
      case "RESET":
        return initState;
      case "INIT": 
        return {
          ...action.payload,
        };
      case "UPDATE_TITLE":
        return {
          ...state,
          title: action.payload,
        };
      case "INIT_TOTALS":
        return {
          ...state,
          totals: action.payload,
        };
      case "UPDATE_TOTAL_REBATE":
        return {
          ...state,
          totals: state.totals.map((total) => {
            if (total.accountTypeId === action.payload.accountTypeId) {
              return {
                ...total,
                rebate: action.payload.rebate,
              };
            }
            return total;
          }),
        };
      case "UPDATE_TOTAL_COMMISSION":
        return {
          ...state,
          totals: state.totals.map((total) => {
            if (total.accountTypeId === action.payload.accountTypeId) {
              return {
                ...total,
                commission: action.payload.commission,
              };
            }
            return total;
          }),
        };
      case "INIT_MEMBERS": 
        return {
          ...state,
          members: action.payload,
        };
      case "UPDATE_MEMBER_REBATE_TOTAL":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId === action.payload.customerId) {
              return {
                ...member,
                values: member.values.map((val) => {
                  if (val.accountTypeId === action.payload.accountTypeId) {
                    return {
                      ...val,
                      rebate: action.payload.rebate,
                      // auto-fill: every product under this account type
                      // takes the same rebate the admin just entered.
                      products: Object.keys(val.products || {}).reduce((acc, prod) => {
                        acc[prod] = {
                          ...val.products[prod],
                          rebate: action.payload.rebate,
                        };
                        return acc;
                      }, {}),
                    };
                  }
                  return val;
                }),
              };
            }
            return member;
          }),
        };
      case "UPDATE_MEMBER_COMMISSION_TOTAL":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId === action.payload.customerId) {
              return {
                ...member,
                values: member.values.map((val) => {
                  if (val.accountTypeId === action.payload.accountTypeId) {
                    return {
                      ...val,
                      commission: action.payload.commission,
                      // auto-fill: every product under this account type
                      // takes the same commission the admin just entered.
                      products: Object.keys(val.products || {}).reduce((acc, prod) => {
                        acc[prod] = {
                          ...val.products[prod],
                          commission: action.payload.commission,
                        };
                        return acc;
                      }, {}),
                    };
                  }
                  return val;
                }),
              };
            }
            return member;
          }),
        };
      case "UPDATE_MEMBER_REBATE_PRODUCT":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId === action.payload.customerId) {
              return {
                ...member,
                values: member.values.map((val) => {
                  if (val.accountTypeId === action.payload.accountTypeId) {
                    return {
                      ...val,
                      products: {
                        ...val.products,
                        [action.payload.product]: {
                          ...val.products?.[action.payload.product],
                          rebate: action.payload.rebate,
                        },
                      },
                    };
                  }
                  return val;
                }),
              };
            }
            return member;
          }),
        };
      case "UPDATE_MEMBER_COMMISSION_PRODUCT":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId === action.payload.customerId) {
              return {
                ...member,
                values: member.values.map((val) => {
                  if (val.accountTypeId === action.payload.accountTypeId) {
                    return {
                      ...val,
                      products: {
                        ...val.products,
                        [action.payload.product]: {
                          ...val.products?.[action.payload.product],
                          commission: action.payload.commission,
                        },
                      },
                    };
                  }
                  return val;
                }),
              };
            }
            return member;
          }),
        };
      case "UPDATE_MEMBER_PAMM_PROFIT_SHARE":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId === action.payload.customerId) {
              return {
                ...member,
                values: member.values.map((val) => {
                  if (val.accountTypeId === action.payload.accountTypeId) {
                    return {
                      ...val,
                      pamProfitShare: action.payload.pamProfitShare,
                    };
                  }
                  return val;
                }),
              };
            }
            return member;
          }),
        };
      case "UPDATE_MEMBER_MARKUP":
        return {
          ...state,
          members: state.members.map((member) => {
            if (member.customerId === action.payload.customerId) {
              return {
                ...member,
                values: member.values.map((val) => {
                  if (val.accountTypeId === action.payload.accountTypeId) {
                    return {
                      ...val,
                      markup: action.payload.markup,
                    };
                  }
                  return val;
                }),
              };
            }
            return member;
          }),
        };
      default: 
        return state;
    }
  }, initState);


  useEffect(() => {
    if (products) {
      const model = {};
      products.forEach((product) => {
        model[product] = {
          rebate: 0,
          commission: 0,
        };
      });
      setProductsModel(model);
    }
  }, [products]);

  useEffect(() => {
    if (accountTypes?.length > 0) {
      const totals = [];
      accountTypes?.forEach((acc) => {
        totals.push({
          accountTypeId: acc?._id,
          rebate: 0,
          commission: 0,
        });
      });
      dispatcher({
        type: "INIT_TOTALS",
        payload: totals,
      });
    }
  }, [accountTypes]);

  useEffect(() => {
    if (parents?.length > 0) {
      const members = [];
      parents?.[0].forEach((parent) => {
        members.push({
          customerId: parent?._id,

          level: parent?.level,
          values: accountTypes?.map((acc) => {
            const isInvestor = acc?.type?.toUpperCase() === "INVESTOR";
          
            const base = {
              accountTypeId: acc?._id,
              rebate: 0,
              commission: 0,
              products: productsModel,
              markup: "",
            };

            if (isInvestor) {
              base.pamProfitShare = 0;
            }
          
            return base;
          }),
        });
      });
      dispatcher({
        type: "INIT_MEMBERS",
        payload: members,
      });
    }
  }, [parents, accountTypes, productsModel]);


  useEffect(() => {
    clientId && dispatch(fetchIbParents({ clientId }));
  }, [clientId]);


  useEffect(() => {

    !submitting && show && setIsAgreementValid(false);
  }, [submitting]);

  useEffect(() => {
    clearingCounter > 0 && show && toggle();
  }, [clearingCounter]);

  useEffect(() => {
    setSubmissionFailure(false);
  }, []);

  useEffect(() => {
    if (show && allAccountTypes && allAccountTypes.length > 0) {
      const accountTypes = new Set();
      allAccountTypes.forEach((acc) => {
        // if (acc.title !== "Demo") {
        //   accountTypes.add(acc);
        // }
        if (acc?.type?.toLowerCase() !== "demo") {
          accountTypes.add(acc);
        }
      });
      setAccountTypes(Array.from(accountTypes));
    }
  }, [allAccountTypes, show]);
  

  useEffect(() => {
    return () => {
      setIsAgreementValid(false);
      dispatcher({
        type: "RESET",
      });
    };
  }, []);

  const {
    title, totals, members
  } = payload;

  const validateForm = () => {
    if (!title) {
      setIsAgreementValid(false);
      setValidationErrorMessage("Please Enter Agreement Title");
      return false;
    }
    if (totals?.length > 0) {
      // Check for null fields first
      let isValid = true;
      totals.forEach((total) => {
        if (total?.commission === null || Number.isNaN(parseFloat(total?.commission)) ||
          total?.rebate === null || Number.isNaN(parseFloat(total?.rebate))) {
          isValid = false;
        }
      });
      if (!isValid) {
        setIsAgreementValid(false);
        setValidationErrorMessage("Please Enter Commission and Rebate");
        return false;
      }
    }
    let isMemberValuesValid = true;
    let isSumValid = true;
    let totalsSum = new Map();
    if (members?.length > 0 && totals?.length > 0) {
      // 1st validation is null
      // 2nd validation is sum of members rebate and commission should be less than or equal to totals
      // 3rd validation should be for each members.value's rebate and commission should not be null and the products rebate and commission should not be null and the sum of products rebate and commission should be less than or equal to members rebate and commission
      for (let i = 0;i < totals.length;i++) {
        totalsSum.set(totals[i].accountTypeId, {
          commission: totals[i].commission,
          rebate: totals[i].rebate,
        });
      }
      for (let i = 0;i < members.length;i++) {
        const member = members[i];
        for (let j = 0; j < member.values.length; j++) {
          const value = member.values[j];
          if (value?.rebate === null || Number.isNaN(parseFloat(value?.rebate)) ||
            value?.commission === null || Number.isNaN(parseFloat(value?.commission))) {
            isMemberValuesValid = false;
            break;
          } else {
            const total = totalsSum.get(value.accountTypeId);
            // subtract the member values from totals if total becomes less than 0 then it is invalid
            if (total) {
              const newTotal = {
                commission: total.commission - value.commission,
                rebate: total.rebate - value.rebate,
              };
              if (newTotal.commission < 0 || newTotal.rebate < 0) {
                isSumValid = false;
                break;
              } else {
                totalsSum.set(value.accountTypeId, newTotal);
              }
            }
          }
        }
        if (!isMemberValuesValid) {
          setIsAgreementValid(false);
          setValidationErrorMessage("Please Enter Commission and Rebate for each member");
          break;
        }
        if (!isSumValid) {
          setIsAgreementValid(false);
          setValidationErrorMessage("Sum of Commission and Rebate for each member should be less than or equal to totals");
          break;
        }
      }

    }
    isMemberValuesValid && isSumValid && setIsAgreementValid(true) & setValidationErrorMessage("");
    return isMemberValuesValid && isSumValid;
  };

  const buildRequestPayload = () => ({
    title,
    totals: totals.map((total) => ({
      ...total,
      rebate: toSafeNumber(total.rebate),
      commission: toSafeNumber(total.commission),
    })),
    members: members.map((member) => ({
      ...member,
      values: member.values.map((val) => ({
        ...val,
        rebate: toSafeNumber(val.rebate),
        commission: toSafeNumber(val.commission),
        pamProfitShare: val.pamProfitShare !== undefined
          ? toSafeNumber(val.pamProfitShare) : undefined,
        products: val.products
          ? Object.fromEntries(
            Object.entries(val.products).map(([prod, pv]) => [
              prod,
              {
                rebate: toSafeNumber(pv.rebate),
                commission: toSafeNumber(pv.commission),
              },
            ])
          )
          : val.products,
      })),
    })),
  });

  if (!show) {
    return null;
  }

  return (
    <Modal
      isOpen={show}
      // toggle={() => {
      //   dispatcher({
      //     type: "RESET",
      //   });
      //   toggle();
      // }}
      centered={true}
      scrollable={true}
      onClosed={() => {
        setSubmissionFailure(false);
      }}
    >
      <ModalHeader toggle={()=>{
        dispatcher({
          type: "RESET",
        });
        toggle();
      }} tag="h4">
        {t("New Shared IB Agreement")}
      </ModalHeader>
      <ModalBody>
        {submissionFailure ? (
          <div
            style={{
              display: "block",
              color: "white",
              marginBottom: 10,
              padding: 10,
              borderRadius: 5,
              backgroundColor: "#f65854",
            }}
            className="formValidationErrMessage"
          >
            {"Invalid values found. Please check the form again!"}
          </div>
        ) : null}
        <AvForm
          validationEvent={["onChange", "onInput", "onBlur"]}
          onValidSubmit={() => {
            setSubmissionFailure(false);
            const valid = validateForm();
            if (valid) {
              dispatch(
                createSharedIbAgreement(buildRequestPayload())
              );
            }
          }}
        >
          <AvField
            name={"title"}
            label={t("Agreement name")}
            className="mb-3"
            type="text"
            value={title}
            errorMessage={t("Required!")}
            validate={{ required: { value: true } }}
            onChange={(e) => {
              dispatcher({
                type: "UPDATE_TITLE",
                payload: e.target.value,
              });
            }}
          />
          <Row className="justify-content-start">
            <Col md="3">{t("Type")}</Col>
            <Col>{t("Total Rebate")}</Col>
            <Col>{t("Total Commission")}</Col>
          </Row>
          {totals?.map((type, accIdx) => (
            <MemoizedAgreementTotals key={accIdx} type={type} accIdx={accIdx} dispatcher={dispatcher}
              agreementName={getAccountType(allAccountTypes, type.accountTypeId)?.title}
            />
          ))}
          <hr className="my-3" />
          <Accordion className="mb-3" alwaysOpen>
            {members &&
              members
                .sort((a, b) => (a.level > b.level ? 1 : -1))
                .map((member, memberIdx) => {
                  const customer = parents?.[0]?.find((p) => p._id === member.customerId);
                  return <>
                    <Accordion.Item key={member.customerId} eventKey={memberIdx}>
                      <Accordion.Header>
                        {customer?.firstName} {customer?.lastName}
                      </Accordion.Header>
                      <Accordion.Body>
                        <Accordion className="my-1" alwaysOpen>
                          {member?.values?.map((type, accIdx) => {
                            const accountType = getAccountType(allAccountTypes, type.accountTypeId);
                            const isInvestor = accountType?.type === "INVESTOR"                        ;                        
                            return (
                              <React.Fragment key={type.accountTypeId}>
                                {isInvestor && (
                                  <MemoizedInvestorForm
                                    type={type}
                                    accIdx={accIdx}
                                    dispatcher={dispatcher}
                                    agreementName={accountType?.title}
                                    memberIdx={memberIdx}
                                    member={member}
                                  />
                                )}
                                <MemoizedAccountForm
                                  type={type}
                                  accIdx={accIdx}
                                  dispatcher={dispatcher}
                                  agreementName={accountType?.title}
                                  memberIdx={memberIdx}
                                  member={member}
                                  products={products}
                                  markups={markups}
                                />
                              </React.Fragment>
                            );
                          })}
                        </Accordion>
                      </Accordion.Body>
                    </Accordion.Item>
                  </>;
                })}
          </Accordion>
          {
            !isAgreementValid && validationErrorMessage && <Alert color="danger">{validationErrorMessage}</Alert>
          }
          {!submitting && submitError && (
            <Alert color="danger" className="mt-2">{submitError}</Alert>
          )}
          <div className="d-flex align-items-center gap-2 mt-3">
            <Button type="submit" disabled={submitting}>
              {t("Submit")}
            </Button>
            {submitting && (
              <span>
                <Spinner size="sm" color="primary" /> {t("Submitting...")}
              </span>
            )}
          </div>
        </AvForm>
      </ModalBody>
    </Modal>
  );
};

export default memo(AddSharedIbModal);
